import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { CiCircleInfo, CiClock2 } from 'react-icons/ci';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { makeCheckoutLink } from '../../../../../firebase/stripe';
import { savePhoneNumber } from '../../../../../firebase/user';
import { alcoholHint, checkGuestFields, GUEST_MESSAGES, guestDigits, guestTextFromSearch } from '../../../../../helper/guests';
import { toE164 } from '../../../../../helper/phone';
import { getConfirmVenue } from '../../../../../store/search';
import { getUser, setShowSignIn, setUser } from '../../../../../store/user';
import { useAllSundaysOpen } from '../../../../../firebase/settings';
import { companyEmail, countDays, isAvailable, isSundayBlocked, useSearchParams } from '../../../../helpers';
import { endTimesFor, isSaturday, startTimesFor, timeLabel, timesProblem } from '../../../../eventTimes';
import { isSunday, venueOpensSundays } from '../../../../sundays';
import { CloseIcon, colors, Field, Input, Label, SmallPeopleIcon } from '../../../../styles';
import PhoneInput from '../../../PhoneInput';
import TermsCheckbox, { BOOKING_DOCUMENTS, TERMS_ERROR } from '../../../TermsCheckbox';
import { makeDisabledDates } from './helpers';

import { TYPE_PAYMENT } from '../../../../../helper/type-payment';
import { useClickBookToGoStripeTag } from '../../../../../tag-manager/booking.tag';
import Calendar from './Calendar';
import Tooltip from './Tooltip';

// NOTE: if you change these, change on functions/index.js & DetailForm.js as well.
const DATE_FORMAT = 'YYYY-MM-DD';
const MAX_DAYS = 14;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${Calendar} {
    align-self: center;
  }
`;

const VenueContent = styled.div`
  display: flex;
  justify-content: none;
  align-items: flex-start;
  margin-bottom: 24px;

  margin-top: -5px;
  padding-bottom: 22px;
  border-bottom: 1px solid ${colors.mediumBorder};
  margin-bottom: 28px;

  & > img {
    flex-shrink: 0;
    flex-grow: 0;
    height: 77px;
    width: 77px;
    object-fit: cover;
    border-radius: 4px;
  }

  & > div {
    flex-shrink: 1;
    flex-grow: 1;
    padding: 0 16px;
    max-width: 251px;
  }

  & > button {
    border: none;
    background-color: transparent;
    padding: 0;
    margin: 0;

    svg {
      height: 14px;
      width: 14px;
    }
  }
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 600;
  margin-top: -5px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const SubTitle = styled.div`
  color: ${colors.fadedText};
  font-size: 12px;
  font-weight: 400;
  margin-top: 4px;

  svg {
    display: inline-block;
    width: 16px;
    height: 16px;
    margin-right: 1px;
    margin-top: -2px;
  }
`;

const PriceTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-top: 16px;
  line-height: 1;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 18px;

  span {
    font-size: 12px;
  }
`;

const ErrorMessage = styled.div`
  margin: -5px 0.833em 16px;
  color: red;
  text-align: center;
`;

const SundayNote = styled.div`
  margin: -5px 0.833em 16px;
  font-size: 13px;
  color: ${colors.fadedText};
  text-align: center;
`;

// Billing B2.1: a phone number is required to book.
const PhoneField = styled(Field)`
  margin: 4px 0 16px;
  padding: 14px 16px;
`;

const PhoneHint = styled.div`
  font-size: 12px;
  color: ${colors.fadedText};
  margin: -8px 0 16px;
`;

const PhoneError = styled.div`
  font-size: 12px;
  color: red;
  margin: -8px 0 16px;
`;

// Billing B4.3 + B0.7a: event start and end time. Copy approved by Braeden, 2026-10-31.
// A heading, the 2 fields with a short hint under each, then the notes in
// their own boxes, so each line can be read alone (Braeden, 2026-10-31).
const TimeSection = styled.div`
  margin: 4px 0 20px;
`;

const TimeHeading = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 500;

  span {
    font-size: 12px;
    font-weight: 400;
    color: ${colors.fadedText};
  }
`;

const TimeFields = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const TimeField = styled(Field)`
  margin: 0 0 6px;
  padding: 12px 14px;
`;

const TimeSelect = styled.select`
  display: block;
  width: 100%;
  margin: 6px 0 0;
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: ${colors.defaultText};
  cursor: pointer;
`;

const TimeHint = styled.div`
  font-size: 11px;
  font-weight: 400;
  line-height: 1.4;
  color: ${colors.fadedText};
  padding: 0 2px;
`;

const TimeError = styled.div`
  font-size: 12px;
  color: red;
  margin: 10px 0 0;
`;

// Rules that limit the times: a soft green box with a clock.
const TimeRule = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 6px;
  background-color: ${colors.primaryBackground};
  font-size: 12px;
  font-weight: 400;
  line-height: 1.45;
  color: ${colors.semiDarkText};

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    margin-top: 1px;
    color: ${colors.primaryButton};
  }

  p {
    margin: 0;
  }

  p + p {
    margin-top: 4px;
  }
`;

// How to change the times: plain small text with an info icon.
const TimeChange = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-top: 12px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.45;
  color: ${colors.fadedText};

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    margin-top: 1px;
  }

  a {
    color: ${colors.primaryButton};
    text-decoration: underline;
  }
`;

// Billing B4.2: guest count and alcohol are required to book.
const AlcoholGroup = styled.fieldset`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 20px;
  margin: 0 0 16px;

  legend {
    float: left;
    width: auto;
    font-size: 14px;
    font-weight: 500;
    margin: 0 4px 0 0;
    padding: 0;
  }
`;

const FieldError = styled(PhoneError)`
  a {
    color: inherit;
    text-decoration: underline;
  }
`;

// The server's words too (functions/helper/checkout.js, MESSAGES); a test checks they match.
const TIME_ERRORS = {
  'no-times': 'Pick a start time and an end time.',
  'end-before-start': 'The end time must be after the start time.',
  'saturday-late': 'On Saturdays for this venue, events end by 11:00 PM, clean-up included.'
};

// The support address in a message becomes a mail link.
const SUPPORT_EMAIL = 'support@yedding.com';
const withMailLink = (text) => {
  const at = text.indexOf(SUPPORT_EMAIL);
  if (at < 0) return text;
  return (
    <>
      {text.slice(0, at)}
      <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
      {text.slice(at + SUPPORT_EMAIL.length)}
    </>
  );
};

const PHONE_ERROR = 'Enter a phone number we can call, like (801) 555-0123. For a number outside the US, start with + and the country code.';

const Buttons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2px;
  border-top: 1px solid ${colors.mediumBorder};
  padding-top: 28px;
`;

const CancelButton = styled.button`
  font-weight: 500;
  color: ${colors.defaultText};
  background-color: transparent;
  font-size: 14px;
  padding: 0;
  padding-bottom: 2px;
  border: none;
  border-bottom: 1px solid ${colors.defaultText};
  line-height: 1;
`;

const SubmitButton = styled.button`
  font-weight: 500;
  color: white;
  background-color: ${colors.primaryButton};
  font-size: 14px;
  border-radius: 4px;
  padding: 15px 0;
  width: 166px;
  border: none;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

const SelectPrice = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: start;
  row-gap: 8px;
  margin-top: 2px;
  border-top: 1px solid ${colors.mediumBorder};
  padding-bottom: 14px;
  padding-top: 20px;

  transition: opacity 0.3s ease, transform 0.3s ease;

  span {
    font-size: 12px;
    /* color: ${colors.fadedText}; */
    font-weight: 400;
  }
  p {
    font-size: 14px;
    font-weight: 500;
  }
`;

const DetailPrice = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: start;
  row-gap: 8px;
  margin-top: 2px;
  border-top: 1px solid ${colors.mediumBorder};
  padding-bottom: 14px;
  padding-top: 20px;

  p {
    font-size: 14px;
    font-weight: 500;
  }

  span {
    font-size: 12px;
    font-weight: 400;
  }

  div {
    display: flex;
    justify-content: space-between;
    width: 100%;
    font-size: 14px;
    font-weight: 500;
  }
`;

const TitleDetailPrice = styled.div`
  font-size: 14px;
  font-weight: 500;
  min-width: max-content;
`;

const Option = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  cursor: pointer;
  user-select: none;

  span {
    font-size: 12px;
  }
`;

const CustomCheckbox = styled.input.attrs({ type: 'radio' })`
  appearance: none;
  width: 18px;
  height: 18px;
  border: 2px solid ${colors.primaryBorder};
  border-radius: 4px;
  cursor: pointer;
  display: grid;
  place-content: center;
  transition: all 0.2s;

  &:checked {
    /* background-color: ${colors.primary}; */
    background-color: ${colors.primaryButton};
    border-color: ${colors.primary};
  }

  &:checked::before {
    content: '✓';
    color: white;
    /* background-color: ${colors.primaryButton}; */
    font-size: 14px;
    line-height: 1;
  }
`;

const WrapperTitle = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

export default function BookModal({ dateRange, onDatePick: _onDatePick, onCancel: _onCancel, onSubmit: _onSubmit }) {
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const confirmVenue = useSelector(getConfirmVenue);

  const [showOptionsMoreSixDays, setShowOptionsMoreSixDays] = useState(false);

  useEffect(() => {
    const today = new Date();
    const { startDate } = dateRange;

    const start = new Date(startDate);
    const diffTime = start - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 6) {
      setShowOptionsMoreSixDays(true);
    } else {
      setShowOptionsMoreSixDays(false);
    }
  }, [dateRange]);

  const showConfirmVenue = !!confirmVenue;

  const maxGuests = confirmVenue?.maxGuests || 0;
  const title = confirmVenue?.name || '';
  const price = Number(confirmVenue?.dayPrice.replace('$', '')).toLocaleString();
  const image = Array.isArray(confirmVenue?.photos) && confirmVenue?.photos.length ? confirmVenue?.photos[0].thumbnail : null;

  const [paymentOption, setPaymentOption] = useState(TYPE_PAYMENT.FULL_PAYMENT);

  const disabledDates = makeDisabledDates(confirmVenue);
  const allSundaysOpen = useAllSundaysOpen();
  const sundaysClosed = !!confirmVenue && !venueOpensSundays(confirmVenue, allSundaysOpen);

  const days = countDays(dateRange);

  // Billing B4.3: times on the venue's clock, as 'HH:MM' text.
  const startDay = dateRange.startDate ? moment(dateRange.startDate).format(DATE_FORMAT) : null;
  const endDay = dateRange.endDate ? moment(dateRange.endDate).format(DATE_FORMAT) : startDay;
  const sundaysOpen = !sundaysClosed;
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [timeError, setTimeError] = useState('');
  const startOptions = startDay ? startTimesFor({ start: startDay, end: endDay, sundaysOpen }) : [];
  const endOptions = startDay ? endTimesFor({ start: startDay, end: endDay, startTime, sundaysOpen }) : [];
  // New dates or a new start can rule out a picked time: clear it, so the booker picks again.
  // Not while the booker is still picking the range (no end day yet).
  const rangeDone = !!dateRange.startDate && !!dateRange.endDate;
  useEffect(() => {
    if (!rangeDone) return;
    if (startTime && !startOptions.includes(startTime)) setStartTime('');
    if (endTime && !endOptions.includes(endTime)) setEndTime('');
  }, [rangeDone, startTime, endTime, startOptions.join(), endOptions.join()]); // eslint-disable-line react-hooks/exhaustive-deps
  const saturdayLimit = !!endDay && !sundaysOpen && isSaturday(endDay);

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  // Only for a signed-in booker with no phone we can call on their profile.
  const needsPhone = !!user?.id && !toE164(user.phoneNumber);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const onPhoneChange = useCallback((text) => {
    setPhone(text);
    setPhoneError('');
  }, []);

  // Billing B4.2. Starts with the search bar's guest count, if any.
  const { guests: searchGuests } = useSearchParams();
  const [guestText, setGuestText] = useState(() => guestTextFromSearch(searchGuests));
  const [alcohol, setAlcohol] = useState(null);
  const [guestError, setGuestError] = useState('');
  const [alcoholError, setAlcoholError] = useState('');
  const onGuestChange = (e) => {
    setGuestText(guestDigits(e.target.value));
    setGuestError('');
  };
  const onAlcoholChange = (value) => {
    setAlcohol(value);
    setAlcoholError('');
  };

  // Billing B4.4: the booker ticks the terms box before Stripe. The server checks too.
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState('');
  const onTermsChange = useCallback((checked) => {
    setTermsAccepted(checked);
    setTermsError('');
  }, []);

  const onDatePick = (...args) => {
    setError('');
    _onDatePick(...args);
  };
  const onCancel = (...args) => {
    setError('');
    _onCancel(...args);
  };
  const onSubmit = (...args) => {
    setError('');
    _onSubmit(...args);
  };

  useEffect(() => {
    if (showConfirmVenue) {
      setPaymentOption(TYPE_PAYMENT.FULL_PAYMENT);
    }
  }, [showConfirmVenue]);

  const onBookConfirm = async () => {
    // Split payment only when the box offers it; the server refuses it otherwise (B2.1).
    const splitOffered = !!confirmVenue?.allowPayAnotherAmount && showOptionsMoreSixDays;
    const typePayment = splitOffered ? paymentOption : TYPE_PAYMENT.FULL_PAYMENT;

    // validate dates
    if (!dateRange.startDate || !dateRange.endDate) {
      setError('Please enter date of reservation');
      return;
    }
    const start = moment(dateRange.startDate);
    const end = moment(dateRange.endDate);
    if (days > MAX_DAYS) {
      setError(`Reservations must be for less than ${MAX_DAYS + 1} days`);
      return;
    }
    if (isSundayBlocked(dateRange, confirmVenue, allSundaysOpen)) {
      setError('This venue does not take bookings on Sundays.');
      return;
    }
    if (!isAvailable(dateRange, confirmVenue, allSundaysOpen)) {
      setError(`Venue is not available within your date selection`);
      return;
    }
    const guests = checkGuestFields({ guestText, alcohol, maxGuests: confirmVenue?.maxGuests });
    const timeProblem = timesProblem({ start: startDay, end: endDay, startTime, endTime, sundaysOpen });
    let phoneNumber = null;
    if (needsPhone) phoneNumber = toE164(phone);
    const phoneBad = needsPhone && !phoneNumber;
    // B4.4: the terms tick is checked with the other fields, so all errors show at once.
    if (guests.guestError || guests.alcoholError || phoneBad || !termsAccepted || timeProblem) {
      setTimeError(timeProblem ? TIME_ERRORS[timeProblem] : '');
      setGuestError(guests.guestError);
      setAlcoholError(guests.alcoholError);
      setPhoneError(phoneBad ? PHONE_ERROR : '');
      setTermsError(termsAccepted ? '' : TERMS_ERROR);
      return;
    }
    // navigate to stripe checkout
    setError('');
    setTimeError('');
    setPhoneError('');
    setIsProcessing(true);

    if (phoneNumber) {
      try {
        await savePhoneNumber(phoneNumber);
        dispatch(setUser({ ...user, phoneNumber }));
      } catch (e) {
        setIsProcessing(false);
        setError('Could not complete. Please try again.');
        return;
      }
    }

    const { url, error, unauthenticated, message } = await makeCheckoutLink({
      venue: confirmVenue,
      start: start.format(DATE_FORMAT),
      end: end.format(DATE_FORMAT),
      startTime,
      endTime,
      typePayment,
      guestCount: guests.guestCount,
      alcohol,
      termsAccepted
    });

    setIsProcessing(false);

    if (unauthenticated) {
      onCancel();
      dispatch(setShowSignIn());
    } else if (error) {
      setError(message || 'Could not complete. Please try again.');
    } else {
      useClickBookToGoStripeTag({
        startDate: start.format(DATE_FORMAT),
        endDate: end.format(DATE_FORMAT),
        venueId: confirmVenue.id,
        venueName: confirmVenue.name,
        typePayment
      });
      onSubmit();
      window.location.href = url;
    }

    localStorage.setItem(
      'booking_status',
      JSON.stringify({
        status: 'initiated',
        venueId: confirmVenue.id,
        venueName: confirmVenue.name,
        label: 'Booking Initiated',
        timestamp: new Date().toISOString()
      })
    );
  };

  const priceTotal = useMemo(() => {
    const price = Number(confirmVenue?.dayPrice.replace('$', ''));
    return (price * days).toFixed(2);
  }, [confirmVenue, days]);

  const priceHalf = useMemo(() => {
    const price = Number(confirmVenue?.dayPrice.replace('$', ''));
    return (price * days * 0.5).toFixed(2);
  }, [confirmVenue, days]);

  return (
    <Modal centered className="date-picker" show={showConfirmVenue} onHide={onCancel}>
      <Modal.Body>
        <ContentWrapper>
          <VenueContent>
            {image && <img src={image} alt={title} />}
            <div>
              <Title>{title}</Title>
              <SubTitle>
                <SmallPeopleIcon />
                Up to {maxGuests} guests
              </SubTitle>
              <PriceTitle>
                {/* No day count until dates are picked (the search bar can start with no dates). */}
                <div>{rangeDone && <>{days} <span>day{days > 1 ? 's ' : ' '}</span></>}</div>
                <div>
                  ${price} <span>/ day</span>
                </div>
              </PriceTitle>
            </div>
            <button onClick={onCancel}>
              <CloseIcon />
            </button>
          </VenueContent>
          <Calendar dateRange={dateRange} onDatePick={onDatePick} disabledDates={disabledDates} disabledDay={sundaysClosed ? isSunday : undefined} />
          {sundaysClosed && !error && <SundayNote>This venue does not take bookings on Sundays.</SundayNote>}
          {!!error && <ErrorMessage>{error}</ErrorMessage>}

          <TimeSection>
            <TimeHeading>
              Event time <span>Venue's local time</span>
            </TimeHeading>
            <TimeFields>
              <div>
                <TimeField hasError={!!timeError && !startTime}>
                  <Label htmlFor="book-start-time">Start time</Label>
                  <TimeSelect
                    id="book-start-time"
                    aria-describedby="book-start-time-help"
                    value={startTime}
                    onChange={(e) => {
                      setStartTime(e.target.value);
                      setTimeError('');
                    }}
                  >
                    <option value="">Pick a time</option>
                    {startOptions.map((t) => (
                      <option key={t} value={t}>
                        {timeLabel(t)}
                      </option>
                    ))}
                  </TimeSelect>
                </TimeField>
                <TimeHint id="book-start-time-help">The first time anyone arrives, including for setup.</TimeHint>
              </div>
              <div>
                <TimeField hasError={!!timeError && !endTime}>
                  <Label htmlFor="book-end-time">End time</Label>
                  <TimeSelect
                    id="book-end-time"
                    aria-describedby="book-end-time-help"
                    value={endTime}
                    onChange={(e) => {
                      setEndTime(e.target.value);
                      setTimeError('');
                    }}
                  >
                    <option value="">Pick a time</option>
                    {endOptions.map((t) => (
                      <option key={t} value={t}>
                        {timeLabel(t)}
                      </option>
                    ))}
                  </TimeSelect>
                </TimeField>
                <TimeHint id="book-end-time-help">The time the last person leaves, after clean-up.</TimeHint>
              </div>
            </TimeFields>
            {!!timeError && <TimeError role="alert">{timeError}</TimeError>}
            {((startDay && endDay !== startDay) || saturdayLimit) && (
              <TimeRule>
                <CiClock2 aria-hidden="true" />
                <div>
                  {startDay && endDay !== startDay && <p>Start time is on the first day. End time is on the last day.</p>}
                  {saturdayLimit && <p>On Saturdays for this venue, events end by 11:00&nbsp;PM, clean-up included.</p>}
                </div>
              </TimeRule>
            )}
            <TimeChange>
              <CiCircleInfo aria-hidden="true" />
              <span>
                To change these times before your event, email <a href={`mailto:${companyEmail}`}>{companyEmail}</a>. We can change them if the host agrees.
              </span>
            </TimeChange>
          </TimeSection>

          {confirmVenue?.allowPayAnotherAmount && showOptionsMoreSixDays && (
            <SelectPrice>
              <TitleDetailPrice>Payment Options:</TitleDetailPrice>
              <Option>
                <CustomCheckbox
                  name="priceOption"
                  value={TYPE_PAYMENT.FULL_PAYMENT}
                  checked={paymentOption === TYPE_PAYMENT.FULL_PAYMENT}
                  onChange={() => setPaymentOption(TYPE_PAYMENT.FULL_PAYMENT)}
                />
                <span>Pay in Full Now</span>
              </Option>
              <Option>
                <CustomCheckbox
                  name="priceOption"
                  value={TYPE_PAYMENT.PARTIAL_PAYMENT}
                  checked={paymentOption === TYPE_PAYMENT.PARTIAL_PAYMENT}
                  onChange={() => setPaymentOption(TYPE_PAYMENT.PARTIAL_PAYMENT)}
                />
                <div>
                  <span>Pay 50% now, and the remaining amount later</span>
                  <Tooltip content="Your final payment will be automatically charged 3 days before your event." position="top">
                    <CiCircleInfo style={{ cursor: 'pointer', marginLeft: '3px' }} />
                  </Tooltip>
                </div>
              </Option>
            </SelectPrice>
          )}

          <DetailPrice>
            <WrapperTitle>
              <TitleDetailPrice>Information on the prices:</TitleDetailPrice>
              {confirmVenue?.allowPayAnotherAmount && (
                <Tooltip content="If you book within 7 days of the event, only full payment is available." position="top">
                  <CiCircleInfo style={{ cursor: 'pointer', marginLeft: '3px' }} />
                </Tooltip>
              )}
            </WrapperTitle>
            <div>
              <span>
                ${price} X {days} days
              </span>
              <span>${priceTotal}</span>
            </div>
            {confirmVenue?.allowPayAnotherAmount && paymentOption === TYPE_PAYMENT.PARTIAL_PAYMENT && showOptionsMoreSixDays && (
              <div>
                <span>First payment, before fees:</span>
                <span>${priceHalf}</span>
              </div>
            )}
          </DetailPrice>

          <PhoneField hasError={!!guestError}>
            <Label htmlFor="book-guests">Number of guests</Label>
            <Input
              id="book-guests"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="Total guests"
              maxLength={7}
              aria-describedby="book-guests-help"
              aria-invalid={!!guestError}
              value={guestText}
              onChange={onGuestChange}
            />
          </PhoneField>
          {guestError ? (
            <FieldError id="book-guests-help">{withMailLink(guestError)}</FieldError>
          ) : (
            <PhoneHint id="book-guests-help">{GUEST_MESSAGES.hint}</PhoneHint>
          )}

          <AlcoholGroup aria-describedby={alcoholError ? 'book-alcohol-error book-alcohol-help' : 'book-alcohol-help'}>
            <legend>Will alcohol be served?</legend>
            <Option>
              <CustomCheckbox name="alcohol" value="yes" checked={alcohol === true} onChange={() => onAlcoholChange(true)} />
              <span>Yes</span>
            </Option>
            <Option>
              <CustomCheckbox name="alcohol" value="no" checked={alcohol === false} onChange={() => onAlcoholChange(false)} />
              <span>No</span>
            </Option>
          </AlcoholGroup>
          {!!alcoholError && <FieldError id="book-alcohol-error">{alcoholError}</FieldError>}
          {/* Billing B3.3: say the alcohol fee, and when it is charged, before payment. */}
          <PhoneHint id="book-alcohol-help">
            {alcoholHint(!!confirmVenue?.allowPayAnotherAmount && showOptionsMoreSixDays && paymentOption === TYPE_PAYMENT.PARTIAL_PAYMENT)}
          </PhoneHint>

          {needsPhone && (
            <>
              <PhoneField hasError={!!phoneError}>
                <Label htmlFor="book-phone">Phone number</Label>
                <PhoneInput id="book-phone" aria-describedby="book-phone-help" aria-invalid={!!phoneError} value={phone} onChange={onPhoneChange} />
              </PhoneField>
              {phoneError ? (
                <PhoneError id="book-phone-help">{phoneError}</PhoneError>
              ) : (
                <PhoneHint id="book-phone-help">We use this number to call if we have questions about your booking.</PhoneHint>
              )}
            </>
          )}

          <TermsCheckbox id="book-terms" checked={termsAccepted} onChange={onTermsChange} documents={BOOKING_DOCUMENTS} error={termsError} />

          <Buttons>
            <CancelButton onClick={onCancel}>Cancel</CancelButton>
            <SubmitButton disabled={isProcessing} onClick={onBookConfirm}>
              {isProcessing ? 'Loading...' : 'Book Now'}
            </SubmitButton>
          </Buttons>
        </ContentWrapper>
      </Modal.Body>
    </Modal>
  );
}
